import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Field, formatIDR } from "../Shared";
import { ProofUpload } from "../ProofUpload";
import { PAY, PAYSET } from "../../constants/testIds";
import { Copy } from "lucide-react";

export function PayDialog({ batch, enrollment: initialEnrollment = null, onClose }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(initialEnrollment);
  const [error, setError] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    api.get("/payment-account/active").then(({ data }) => setAccount(data.data)).catch(() => {});
  }, []);

    useEffect(() => {
    if (enrollment) return;
    api.post("/enrollments", { batch_id: batch.batch_id })
      .then(({ data }) => setEnrollment(data.data))
      .catch(async (err) => {
        const { code, message } = extractError(err);
        if (code === "ENROLLMENT_EXISTS") {
          try {
            const { data } = await api.get("/enrollments/me");
            const existing = data.data.find((e) => e.batch_id === batch.batch_id && e.status === "PENDING_PAYMENT");
            if (existing) return setEnrollment(existing);
            return setError(t("payments.alreadyEnrolled"));
          } catch (err) {
            console.error("enrollment lookup failed", err);
          }
        }
        setError(message);
      });
  }, [batch.batch_id, enrollment, t]);

  const copy = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error(t("payments.copyFailed"));
    }
  };

  const submit = async (e) => {    e.preventDefault();
    if (!fileId) return toast.error(t("payments.proofRequired"));
    if (!account) return toast.error(t("paymentSettings.notSet"));
    setBusy(true);
    try {
      await api.post("/payments", { enrollment_id: enrollment.enrollment_id, proof_file_id: fileId, note: note || null });
      toast.success(t("payments.submitted"));
      onClose();
      navigate("/my-journey");
    } catch (err) {
      toast.error(extractError(err).message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={PAY.dialog} className="bg-[#161923] border-white/10 text-slate-200">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-white">{t("payments.payTitle")}</DialogTitle>
          <DialogDescription className="text-slate-400">{batch.name}</DialogDescription>
        </DialogHeader>
        {error ? (
          <div data-testid={PAY.enrollError} className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
        ) : !enrollment ? (
          <div className="text-sm text-slate-400">{t("common.loading")}</div>
        ) : (
          <>
            <div className="rounded-lg bg-[#0B0C13] border border-white/5 p-4 text-sm text-slate-300 space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">{t("payments.participantCode")}</span><span data-testid={PAY.enrollCode} className="font-mono font-bold text-emerald-300">{enrollment.participant_code}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("payments.baseFee")}</span><span>{formatIDR(enrollment.base_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("payments.uniqueCode")}</span><span>+ Rp{enrollment.unique_code}</span></div>
              <div className="flex justify-between border-t border-white/5 pt-2"><span className="text-white font-semibold">{t("payments.totalToPay")}</span><span data-testid={PAY.enrollTotal} className="text-[#F59E0B] font-bold text-lg">{formatIDR(enrollment.total_amount)}</span></div>
              {account ? (
                <div data-testid={PAYSET.destination} className="pt-1 space-y-1.5">
                  <p className="text-xs text-slate-500">{t("payments.transferTo")}: <span className="text-white">{account.bank_name} {account.account_number} · {account.display_label || account.account_holder_name}</span></p>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" data-testid={PAYSET.copyAccount} size="sm" variant="outline" onClick={() => copy(account.account_number, t("payments.copiedAccount"))} className="h-8 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white text-xs">
                      <Copy className="h-3.5 w-3.5 mr-1" /> {t("payments.copyAccount")}
                    </Button>
                    <Button type="button" data-testid={PAYSET.copyAmount} size="sm" variant="outline" onClick={() => copy(String(enrollment.total_amount), t("payments.copiedAmount"))} className="h-8 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white text-xs">
                      <Copy className="h-3.5 w-3.5 mr-1" /> {t("payments.copyAmount")}
                    </Button>
                  </div>
                </div>
              ) : (
                <p data-testid={PAYSET.destinationMissing} className="text-xs text-red-300 pt-1">{t("paymentSettings.notSet")}</p>
              )}
              <p className="text-xs text-amber-300">{t("payments.exactAmountHint")}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <ProofUpload testId={PAY.proofFile} onUploaded={setFileId} />
              <Field label={t("payments.note")} testId={PAY.noteInput} value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-white/5">{t("common.cancel")}</Button>
                <Button data-testid={PAY.submitBtn} type="submit" disabled={busy || !fileId || !account} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{busy ? t("common.loading") : t("payments.submit")}</Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
